import { Fragment, useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { track, EVENTS } from '@/utils/analytics'
import {
  X,
  Calendar,
  Clock,
  PlaneTakeoff,
  PlaneLanding,
  Plus,
  TriangleAlert,
  AlertCircle,
  Ellipsis,
  Trash2,
  Globe,
  MoreVertical
} from '@/components/icons'
import { hapticLight } from '@/lib/haptics'
import { MenuItem } from './ui/menu-item'
import { SectionTitle } from './ui/section-title'
import { SwipeableRow } from './ui/swipeable-row'
import { Button } from './ui/button'
import { PrntrCard, PrntrInput, PrntrValue } from './ui/prntr-input'
import { DateObjectRow, PickerRowField } from './ui/prntr-fields'
import { ResizableDrawer } from './ui/resizable-drawer'
import { Stepper } from './ui/stepper'
import { useIsDesktop } from './ui/use-mobile'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from './ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog'
import { Avatar, AvatarFallback } from './ui/avatar'
import { cn, getInitials, formatCrewRole } from '../lib/utils'
import {
  useTripGetById,
  useTripLegAdd,
  useTripLegUpdate,
  useTripLegDelete,
  useTripLegGetById,
  useTripCheckLegOverlap,
  useTripLegGetAvailableCrew,
  useAirportByIcao,
  usePassengerGetAll,
  useTasksByTripLeg,
  useMaintenanceGetByAircraft,
  useAircraftGetAll,
  useAircraftGetById,
  useTripLegUpdateComplianceDocuments,
  useDocumentCreate,
  useDocumentGetByIds,
} from '@/lib/hooks'
import { useS3GetUploadPost, generateComplianceS3Key, uploadFileWithPresignedPost } from '@/lib/hooks/s3'
import { useCompanyGet } from '@/lib/hooks/company'
import { useOrpcClient } from '@/lib/orpc'
import {
  exportPassengerManifestPdf,
  formatManifestName,
} from '@/lib/passenger-manifest-pdf'
import { DocumentSubType } from '@@api/shared'
import { TripAddDocumentDrawer } from './TripAddDocumentDrawer'
import { DocumentFormDrawer } from './documents/DocumentFormDrawer'
import type { StagedDocument } from './documents/types'
import { AddComplianceDocumentToTripDrawer } from './AddComplianceDocumentToTripDrawer'
import { ViewDocumentDrawer } from './documents/ViewDocumentDrawer'
import { AircraftPickerDrawer } from './AircraftPickerDrawer'
import { useIsAdmin } from '@/hooks/useIsCompanyAdmin'
import { useIsPilot } from '@/hooks/useIsPilot'
import { useCurrentCrewMember } from '@/hooks/useCurrentCrewMember'
import { FlightLogDrawer } from './FlightLogDrawer'
import {
  FlightLogStatusCard,
  type FlightLogCardPostFlight,
  type FlightLogCardRouteTimes,
} from './FlightLogStatusCard'
import { useFlightLogGetForTripLeg } from '@/lib/hooks/flightLog'
import { deriveFlightLogLifecycle, isFlightLogOpenForLeg } from '@@api/flightLog.shared'
import { TaskPickerDrawer } from './TaskPickerDrawer'
import { useTaskAttach, useTaskDetach } from '@/lib/hooks/task'
import { useChecklistTemplates } from '@/lib/hooks/checklist'
import { getAirportByIcao } from '@/lib/airport-db'
import { estimateArrivalDateTime } from '@/lib/flight-time'
import { findMaintenanceConflict, formatUtcOffsetLabel } from '@/lib/flight-utils'
import dayjs from 'dayjs'
import { AirportPickerDrawer } from './AirportPickerDrawer'
import { FboSection } from './FboSection'
import { AirportDetailDrawer } from './AirportDetailDrawer'
import { CrewMemberPickerDrawer } from './CrewMemberPickerDrawer'
import { PassengerPickerDrawer, UnknownPaxRow } from './PassengerPickerDrawer'
import { PaxDrawer } from './PaxDrawer'
import { PaxOnboardingCard } from './PaxOnboardingCard'
import { CrewBasicDetailDrawer } from './CrewBasicDetailDrawer'
import { CrewAcceptanceBadge } from './CrewAcceptanceBadge'
import { PaxBasicDetailDrawer } from './PaxBasicDetailDrawer'
import { TimeInput } from './TimeInput'
import { Skeleton } from './ui/skeleton'
import { Card } from './ui/card'
import { AlertBadge } from './ui/alert-badge'
import { EmptyStateCard } from './EmptyStateCard'
import { TaskStatusBadge } from './TaskStatusBadge'
import { IdCardLanyard, User, ClipboardCheck, CirclePlus, CircleMinus, Plane, FileText, File } from './icons'
import { StatCard } from './ui/stat-card'
import { SearchInput } from './ui/search-input'
import { DutyBadge } from './duty-status'
import { ListSkeleton } from './ui/skeleton-presets'
import { useRosterUpdate } from '../lib/hooks/roster'

// "Info" label pill on the airport rows — opens the airport info drawer
// without unmounting the flight form (a full-page navigate would lose edits).
function AirportInfoPill({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      className="active:opacity-70"
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      Info
    </button>
  )
}

// Browser-local Date carrying the UTC wall clock of a leg end's instant
// (see the departureUtcDisplay memo for why and for the fallback rules).
function utcWallClock(
  merged: Date | undefined,
  time: Date | null,
  date: Date | null,
  tz: string | undefined
) {
  if (!merged && !time) return null
  const instant =
    merged ??
    dayjs
      .tz(
        `${(date ? dayjs(date) : dayjs()).format('YYYY-MM-DD')} ${dayjs(time).format('HH:mm')}`,
        tz ?? 'UTC'
      )
      .toDate()
  const u = dayjs.utc(instant)
  return dayjs().hour(u.hour()).minute(u.minute()).second(0).millisecond(0).toDate()
}

// Bare TimeInput styling for PRNTR rows (mirrors ClockRow in ui/prntr-fields)
const bareTimeInputClass =
  'h-auto w-20 shrink-0 grow-0 rounded-none border-0 bg-transparent p-0 text-right font-semibold shadow-none focus-visible:ring-0 focus-visible:border-transparent [&::-webkit-date-and-time-value]:text-right'

// Form validation schema - separate date and time fields
const tripLegFormSchema = z.object({
  aircraftId: z.string().min(1, 'Aircraft is required'),
  departureAirportIcao: z.string().min(1, 'Departure airport is required'),
  // invalid_type_error covers cleared fields (null); required_error covers undefined
  departureDate: z.date({ required_error: 'Departure date is required', invalid_type_error: 'Departure date is required' }),
  departureTime: z.date({ required_error: 'Departure time is required', invalid_type_error: 'Departure time is required' }),
  arrivalAirportIcao: z.string().min(1, 'Arrival airport is required'),
  arrivalDate: z.date({ required_error: 'Arrival date is required', invalid_type_error: 'Arrival date is required' }),
  arrivalTime: z.date({ required_error: 'Arrival time is required', invalid_type_error: 'Arrival time is required' }),
  paxIds: z.array(z.string()).default([]),
  // Passengers whose identity is not yet known (count only)
  unknownPaxCount: z.number().int().min(0).default(0),
  crewIds: z.array(z.string()).default([]),
  // FBO / handling agent per end (optional)
  departureFboId: z.string().nullable().default(null),
  arrivalFboId: z.string().nullable().default(null),
})

type TripLegFormData = z.infer<typeof tripLegFormSchema>

type Step = 1 | 2 | 3 | 4 | 5

const STEP_LABELS: Record<Step, string> = {
  1: 'Flight',
  2: 'Crew',
  3: 'PAX',
  4: 'Tasks',
  5: 'Docs',
}

function TripLegSummary({
  tasksCount,
  crewCount,
  paxCount,
}: {
  tasksCount: number
  crewCount: number
  paxCount: number
}) {
  const cards = [
    { label: 'Tasks', icon: ClipboardCheck, value: tasksCount },
    { label: 'Crew', icon: IdCardLanyard, value: crewCount },
    { label: 'PAX', icon: IdCardLanyard, value: paxCount },
  ]
  return (
    <div className="flex items-center gap-3 px-4 pb-4 shrink-0">
      {cards.map(({ label, icon: Icon, value }) => (
        <StatCard
          key={label}
          variant="default"
          className="flex-1 min-w-0"
          label={label}
          value={value}
          icon={<Icon />}
        />
      ))}
    </div>
  )
}

function countRespondedItems(
  items: Array<{ booleanResponse: boolean | null; textResponse: string | null; numberResponse: number | null }>
): number {
  return items.filter(
    (i) => i.booleanResponse !== null || i.textResponse !== null || i.numberResponse !== null
  ).length
}

interface FlightDrawerProps {
  isOpen: boolean
  onClose: () => void
  tripId: string
  tripLegId?: string // If provided, drawer opens in edit mode
  mode?: 'create' | 'edit' // 'create' = single page, 'edit' = multi-step wizard
  onSuccess?: (id: string) => void // Called after successful create/update
  readOnly?: boolean // If true, drawer opens in view-only mode
  // When provided, the drawer renders as a resizable floating bottom sheet
  // (no backdrop) instead of the modal drawer.
  snapPoints?: Array<number | string>
  defaultSnapPoint?: number | string
  activeSnapPoint?: number | string | null
  setActiveSnapPoint?: (s: number | string | null) => void
  // When set, the header globe button toggles the map instead of navigating to
  // the flight route.
  onToggleMap?: () => void
  // Whether the map/globe backdrop is currently shown behind the drawer. In edit
  // mode the dim overlay is rendered only when the map is hidden.
  isMapVisible?: boolean
}

export function FlightDrawer({
  isOpen,
  onClose,
  tripId,
  tripLegId,
  mode,
  readOnly = false,
  snapPoints,
  defaultSnapPoint,
  activeSnapPoint,
  setActiveSnapPoint,
  onToggleMap,
  isMapVisible = false,
}: FlightDrawerProps) {
  const isDesktop = useIsDesktop()
  const isEditMode = mode === 'edit' || (mode === undefined && !!tripLegId)
  const isCreateMode = mode === 'create' || (mode === undefined && !tripLegId)

  // The drawer is always presented as a resizable floating bottom sheet.
  // Call sites that don't pass `snapPoints` get a sensible default. Create mode
  // opens at the compact 0.6 snap (its short wizard content fits without a gap);
  // edit/view open near full-height (the last snap).
  const resolvedSnapPoints = snapPoints ?? [0.3, 0.5, 0.6, 0.9]
  const [internalSnap, setInternalSnap] = useState<number | string | null>(
    defaultSnapPoint ??
      (isCreateMode ? 0.6 : resolvedSnapPoints[resolvedSnapPoints.length - 1]),
  )
  const activeSnap = activeSnapPoint ?? internalSnap
  const setActiveSnap = (next: number | string) => {
    if (activeSnapPoint == null) setInternalSnap(next)
    setActiveSnapPoint?.(next)
  }

  // Step state
  const [currentStep, setCurrentStep] = useState<Step>(1)

  // Fetch trip leg data if in edit mode
  const { data: tripLegData, isLoading: isTripLegLoading } = useTripLegGetById(tripLegId)
  const { data: tripData } = useTripGetById(tripId)
  // Company name for the passenger manifest "Owner or Operator" field
  const { data: companyData } = useCompanyGet()
  const orpcClient = useOrpcClient()
  const [isSharingManifest, setIsSharingManifest] = useState(false)

  // Mutations
  const addTripLeg = useTripLegAdd()
  const updateTripLeg = useTripLegUpdate()
  const deleteTripLeg = useTripLegDelete()
  const attachTask = useTaskAttach()
  const detachTask = useTaskDetach()
  const updateRoster = useRosterUpdate()
  const updateLegDocuments = useTripLegUpdateComplianceDocuments()
  const createDocument = useDocumentCreate()
  const getUploadPost = useS3GetUploadPost()

  // Desired set of template IDs attached to this leg (edit mode only).
  // Seeded from the server `tasks` and mutated by TaskPickerDrawer and swipe-to-delete.
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([])
  // Orphan attached tasks (no checklistTemplateId) queued for detach on save.
  const [removedOrphanTaskIds, setRemovedOrphanTaskIds] = useState<string[]>([])

  // Delete confirmation dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  // Picker Drawers
  const [isAircraftPickerOpen, setIsAircraftPickerOpen] = useState<boolean>(false)
  const [isDepartureAirportPickerOpen, setIsDepartureAirportPickerOpen] =
    useState<boolean>(false)
  const [isArrivalAirportPickerOpen, setIsArrivalAirportPickerOpen] =
    useState<boolean>(false)
  // Airport whose info drawer is open (the "Airport Info" input segment).
  const [infoIcao, setInfoIcao] = useState<string | null>(null)

  // Crew & PAX picker drawer state
  const [isCrewPickerOpen, setIsCrewPickerOpen] = useState(false)
  const [isPaxPickerOpen, setIsPaxPickerOpen] = useState(false)
  const [isTaskPickerOpen, setIsTaskPickerOpen] = useState(false)
  const [crewDetailId, setCrewDetailId] = useState<string | null>(null)
  const [paxDetailId, setPaxDetailId] = useState<string | null>(null)
  // Create a passenger on the fly from the PAX selection step (create mode)
  const [isAddPaxOpen, setIsAddPaxOpen] = useState(false)

  // Flight documents: sub-drawers, viewer, remove confirmation (edit mode)
  const [isAddDocumentChooserOpen, setIsAddDocumentChooserOpen] = useState(false)
  const [isUploadDocumentOpen, setIsUploadDocumentOpen] = useState(false)
  const [isAttachDocumentsOpen, setIsAttachDocumentsOpen] = useState(false)
  const [viewDocumentId, setViewDocumentId] = useState<string | null>(null)
  // Separate open flag; the id is kept on close so the view drawer can play
  // its exit animation (the next tap overwrites it).
  const [isViewDocumentOpen, setIsViewDocumentOpen] = useState(false)
  const [removeDocTarget, setRemoveDocTarget] = useState<{ id: string; title: string } | null>(null)
  // Create wizard: documents staged locally, uploaded + linked after the leg is created
  const [stagedDocuments, setStagedDocuments] = useState<StagedDocument[]>([])
  const [stagedExistingDocIds, setStagedExistingDocIds] = useState<string[]>([])
  // Titles of staged attach-existing docs (create mode list rendering)
  const { data: stagedExistingDocs = [] } = useDocumentGetByIds(stagedExistingDocIds)

  // Search queries for the inline crew/pax lists (create mode only)
  const [crewSearch, setCrewSearch] = useState('')
  const [paxSearch, setPaxSearch] = useState('')

  // Off-duty crew pending an "add on-duty" confirmation (admin override, create-mode inline list)
  const [assignTarget, setAssignTarget] = useState<
    { id: string; name: string; otherAircraftRegistration?: string | null } | null
  >(null)
  // Crew the admin chose to put on duty — the roster entry is written on flight save, not now.
  const [pendingOnDutyCrewIds, setPendingOnDutyCrewIds] = useState<Set<string>>(new Set())

  const navigate = useNavigate()
  const { isAdmin } = useIsAdmin()
  const { isPilot } = useIsPilot()
  const { data: currentCrew } = useCurrentCrewMember()

  // Flight log (edit mode; visible to admins + pilots only, editable by pilots).
  // The enabled-gate keeps ineligible users from ever calling the procedure.
  const [isFlightLogOpen, setIsFlightLogOpen] = useState(false)
  const { data: flightLogData } = useFlightLogGetForTripLeg(
    isEditMode && (isAdmin || isPilot) ? tripLegId : undefined
  )
  const flightLog = flightLogData?.flightLog

  // Attached tasks (edit mode only — requires a tripLegId)
  const {
    data: tasks = [],
    isSuccess: tasksLoaded,
    refetch: refetchTasks,
  } = useTasksByTripLeg(isEditMode && tripLegId ? tripLegId : null)

  // Force a refetch every time the drawer opens, so progress/status reflects any
  // checklist edits the user made on the task page since this drawer was last opened.
  useEffect(() => {
    if (isOpen && isEditMode && tripLegId) {
      refetchTasks()
    }
  }, [isOpen, isEditMode, tripLegId, refetchTasks])

  // All checklist templates (used to render "pending" rows in the task list for templates
  // added via the picker but not yet saved).
  const { data: allTemplates = [] } = useChecklistTemplates()

  const originalTemplateIds = useMemo(
    () => tasks.map((t) => t.checklistTemplateId).filter((v): v is string => !!v),
    [tasks]
  )

  // Seed `selectedTemplateIds` from the server state exactly once per open session,
  // and only after the tasks query has actually loaded. Re-seeding on every
  // `originalTemplateIds` reference change (e.g. window-focus refetches) would
  // otherwise wipe out the user's picker selections.
  const seededForOpenRef = useRef(false)
  useEffect(() => {
    if (!isOpen) {
      seededForOpenRef.current = false
      return
    }
    if (seededForOpenRef.current) return
    if (!tasksLoaded) return
    setSelectedTemplateIds(originalTemplateIds)
    setRemovedOrphanTaskIds([])
    seededForOpenRef.current = true
  }, [isOpen, tasksLoaded, originalTemplateIds])

  const hasTaskChanges =
    selectedTemplateIds.length !== originalTemplateIds.length ||
    selectedTemplateIds.some((id) => !originalTemplateIds.includes(id)) ||
    removedOrphanTaskIds.length > 0

  const orphanTasks = useMemo(
    () => tasks.filter((t) => !t.checklistTemplateId && !removedOrphanTaskIds.includes(t.id)),
    [tasks, removedOrphanTaskIds]
  )
  const visibleTaskCount = selectedTemplateIds.length + orphanTasks.length

  // Tracks whether the user has touched any schedule input since the drawer opened.
  // Gates the auto-arrival effect so edit mode's seeded values aren't overwritten on load.
  const userTouchedScheduleRef = useRef(false)

  // Tracks which swipeable row is currently revealed (only one at a time).
  const [openRowId, setOpenRowId] = useState<string | null>(null)

  const defaultValues: TripLegFormData = {
    aircraftId: '',
    arrivalAirportIcao: '',
    arrivalDate: null as unknown as Date,
    arrivalTime: null as unknown as Date,
    departureAirportIcao: '',
    departureDate: null as unknown as Date,
    departureTime: null as unknown as Date,
    paxIds: [],
    unknownPaxCount: 0,
    crewIds: [],
    departureFboId: null,
    arrivalFboId: null,
  }

  // Compute values for edit mode - memoized to prevent isDirty from resetting on re-renders
  // Convert stored UTC datetimes to airport-local for display in the form
  const editValues: TripLegFormData | undefined = useMemo(() => {
    if (!isEditMode || !tripLegData) return undefined
    const depAirport = getAirportByIcao(tripLegData.departureAirportIcao)
    const arrAirport = getAirportByIcao(tripLegData.arrivalAirportIcao)
    const depTz = depAirport?.timezone ?? 'UTC'
    const arrTz = arrAirport?.timezone ?? 'UTC'
    const depLocal = dayjs.utc(tripLegData.departureDateTime).tz(depTz)
    const arrLocal = dayjs.utc(tripLegData.arrivalDateTime).tz(arrTz)
    // Create Dates whose browser-local hour/minute match the airport-local values,
    // since TimeInput/DatePicker format using browser-local time (dayjs(value).format(...))
    return {
      aircraftId: tripLegData.aircraftId,
      arrivalAirportIcao: tripLegData.arrivalAirportIcao,
      arrivalDate: dayjs(arrLocal.format('YYYY-MM-DD')).startOf('day').toDate(),
      arrivalTime: dayjs().hour(arrLocal.hour()).minute(arrLocal.minute()).second(0).millisecond(0).toDate(),
      departureAirportIcao: tripLegData.departureAirportIcao,
      departureDate: dayjs(depLocal.format('YYYY-MM-DD')).startOf('day').toDate(),
      departureTime: dayjs().hour(depLocal.hour()).minute(depLocal.minute()).second(0).millisecond(0).toDate(),
      paxIds: tripLegData.passengers.map((p) => p.id),
      unknownPaxCount: tripLegData.unknownPaxCount ?? 0,
      crewIds: tripLegData.crew?.map((c) => c.id) || [],
      departureFboId: tripLegData.departureFboId ?? null,
      arrivalFboId: tripLegData.arrivalFboId ?? null,
    }
  }, [isEditMode, tripLegData])

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isValid, isDirty, isSubmitting },
  } = useForm<TripLegFormData>({
    resolver: zodResolver(tripLegFormSchema),
    values: editValues ?? defaultValues,
    resetOptions: {
      keepDirtyValues: false,
    },
    mode: 'onChange',
  })

  const watchedAircraftId = watch('aircraftId')
  const arrivalAirportIcao = watch('arrivalAirportIcao')
  const arrivalDate = watch('arrivalDate')
  const arrivalTime = watch('arrivalTime')
  const departureAirportIcao = watch('departureAirportIcao')
  const departureDate = watch('departureDate')
  const departureTime = watch('departureTime')
  const paxIds = watch('paxIds')
  const unknownPaxCount = watch('unknownPaxCount')
  const crewIds = watch('crewIds')
  const departureFboId = watch('departureFboId')
  const arrivalFboId = watch('arrivalFboId')

  // Get airport info for display
  const arrivalAirport = useAirportByIcao(arrivalAirportIcao)
  const departureAirport = useAirportByIcao(departureAirportIcao)

  // Compute merged DateTimes for overlap check (using airport timezones)
  const mergedDepartureDateTime = useMemo(() => {
    if (!departureDate || !departureTime || !departureAirport) return undefined
    const depTz = departureAirport.timezone
    return dayjs.tz(
      `${dayjs(departureDate).format('YYYY-MM-DD')} ${dayjs(departureTime).format('HH:mm')}`,
      depTz
    ).toDate()
  }, [departureDate, departureTime, departureAirport])

  const mergedArrivalDateTime = useMemo(() => {
    if (!arrivalDate || !arrivalTime || !arrivalAirport) return undefined
    const arrTz = arrivalAirport.timezone
    return dayjs.tz(
      `${dayjs(arrivalDate).format('YYYY-MM-DD')} ${dayjs(arrivalTime).format('HH:mm')}`,
      arrTz
    ).toDate()
  }, [arrivalDate, arrivalTime, arrivalAirport])

  // Browser-local Dates carrying the UTC wall clock, so TimeInput (which
  // formats with dayjs(value).format) displays the UTC time — same trick as
  // editValues above. Derived from the merged instants; the RHF fields stay
  // the single source of truth. Before an airport/date is picked the merged
  // instant doesn't exist, so fall back to today's date and a zero offset —
  // the field still mirrors the local time and re-derives once they're set.
  const departureUtcDisplay = useMemo(
    () =>
      utcWallClock(
        mergedDepartureDateTime,
        departureTime,
        departureDate,
        departureAirport?.timezone
      ),
    [mergedDepartureDateTime, departureTime, departureDate, departureAirport?.timezone]
  )

  const arrivalUtcDisplay = useMemo(
    () => utcWallClock(mergedArrivalDateTime, arrivalTime, arrivalDate, arrivalAirport?.timezone),
    [mergedArrivalDateTime, arrivalTime, arrivalDate, arrivalAirport?.timezone]
  )

  // A UTC edit is interpreted against the current UTC calendar date of the
  // merged instant (falling back to today's while no airport/date is picked),
  // then converted back to airport-local — which can move the local DATE
  // across midnight, so the date is rewritten too, but only once one exists:
  // a UTC edit never fills an empty date field. Clearing is owned by the
  // local field (the UTC display refills from the merged instant).
  const applyUtcTimeEdit = (
    picked: Date | null,
    merged: Date | undefined,
    tz: string | undefined,
    dateField: 'departureDate' | 'arrivalDate',
    timeField: 'departureTime' | 'arrivalTime',
  ) => {
    if (!picked) return
    const anchor = merged ? dayjs.utc(merged) : dayjs.utc()
    const newUtc = dayjs.utc(`${anchor.format('YYYY-MM-DD')} ${dayjs(picked).format('HH:mm')}`)
    const local = newUtc.tz(tz ?? 'UTC')
    const opts = { shouldValidate: true, shouldDirty: true, shouldTouch: true }
    if (merged) {
      setValue(dateField, dayjs(local.format('YYYY-MM-DD')).startOf('day').toDate(), opts)
    }
    setValue(
      timeField,
      dayjs().hour(local.hour()).minute(local.minute()).second(0).millisecond(0).toDate(),
      opts
    )
  }

  // Reactively check for leg overlap when all date/time fields are set
  const { data: overlapCheck, isLoading: isOverlapChecking } = useTripCheckLegOverlap({
    tripId,
    departureDateTime: mergedDepartureDateTime,
    arrivalDateTime: mergedArrivalDateTime,
    excludeLegId: tripLegId,
  })

  const hasOverlap = overlapCheck?.hasOverlap ?? false
  const overlapMessage = hasOverlap
    ? `Overlaps with: ${overlapCheck!.conflictingRoutes.join(', ')}`
    : undefined

  // Aircraft is per-flight. Default a new leg to the trip's latest flight's
  // aircraft (continuity for multi-leg trips); for a trip with no flights yet,
  // fall back to the company default aircraft.
  const { data: aircraftList } = useAircraftGetAll()
  const defaultAircraftId =
    tripData?.legs?.[tripData.legs.length - 1]?.aircraftId ??
    aircraftList?.find((a) => a.isDefault)?.id
  useEffect(() => {
    if (isOpen && isCreateMode && defaultAircraftId && !watchedAircraftId) {
      setValue('aircraftId', defaultAircraftId, { shouldValidate: true })
    }
  }, [isOpen, isCreateMode, defaultAircraftId, watchedAircraftId, setValue])

  // Registration label for the picker button and drawer title
  const { data: selectedAircraft } = useAircraftGetById(watchedAircraftId)

  // Block legs whose departure date falls in a maintenance window for this
  // flight's aircraft.
  const { data: maintenanceRecords } = useMaintenanceGetByAircraft(
    watchedAircraftId ?? ''
  )
  const maintenanceConflict = useMemo(
    () => findMaintenanceConflict(maintenanceRecords ?? [], departureDate),
    [maintenanceRecords, departureDate]
  )
  const maintenanceMessage = maintenanceConflict
    ? `Aircraft is in scheduled maintenance ${dayjs.utc(maintenanceConflict.startDate).format('MMM D')} – ${dayjs.utc(maintenanceConflict.endDate).subtract(1, 'day').format('MMM D')}. Pick a date on or after ${dayjs.utc(maintenanceConflict.endDate).format('MMM D')}.`
    : undefined

  const isCompleted = tripData?.tripState === 'Complete'
  // Draft legs aren't published, so deleting one is a "remove" (no crew notified),
  // whereas deleting a published leg is a "cancel". Drives the user-facing copy.
  const isDraft = tripData?.tripState === 'Draft'
  const removeFlightLabel = isDraft ? 'Remove Flight' : 'Cancel Flight'
  // The flight log is transferred from the paper folio after the flight, so
  // the leg only accepts a log once it has departed (server enforces the same
  // rule in upsertDraft/submit). A pre-existing log on a not-yet-departed leg
  // stays viewable but read-only.
  const canLogLeg = !isDraft && !!tripLegData && isFlightLogOpenForLeg(tripLegData)
  // Only the pilots crewed on this leg may fill in, sign and submit its log.
  // Other pilots (and admins) get a read-only view. Server enforces the same
  // rule in upsertDraft/submit.
  const isPilotOnLeg =
    isPilot &&
    !!currentCrew &&
    (tripLegData?.crew ?? []).some((c: { id: string }) => c.id === currentCrew.id)

  // Available crew for inline selection (Step 2). Leg datetimes let the backend
  // check flight conflicts by time overlap rather than the whole departure day.
  const { data: availableCrewData, isPending: isAvailableCrewLoading } =
    useTripLegGetAvailableCrew({
      startDate: departureDate,
      departureDateTime: mergedDepartureDateTime,
      arrivalDateTime: mergedArrivalDateTime,
      excludeLegId: tripLegId,
      aircraftId: watchedAircraftId || undefined,
    })

  // Look-up of every crew the selected list might reference: available, the date's unavailable
  // crew (so admin-overridden off-duty/other-aircraft picks still render), plus any assigned crew
  // missing from both (e.g. inactive). An overridden crew (pending on-duty) is treated as available
  // so it shows without an "unavailable" warning and doesn't block save.
  const mergedCrew = useMemo(() => {
    const available = availableCrewData?.availableCrew || []
    const unavailable = availableCrewData?.unavailableCrew || []
    const knownIds = new Set([
      ...available.map(({ crew }) => crew.id),
      ...unavailable.map(({ crew }) => crew.id),
    ])

    // In edit mode, include assigned crew absent from both partitions (inactive/deleted) so they
    // stay visible and flagged unavailable.
    const unavailableAssigned: { crew: { id: string; name: string; firstName?: string | null; lastName?: string | null; crewRole: string; avatarColor?: string | null } }[] = []
    if (isEditMode && tripLegData?.crew) {
      for (const c of tripLegData.crew) {
        if (!knownIds.has(c.id)) {
          unavailableAssigned.push({ crew: c })
        }
      }
    }

    return [
      ...available.map(({ crew }) => ({ crew, unavailable: false })),
      ...unavailable.map(({ crew }) => ({
        crew,
        // Overridden crew (pending on-duty) will be made available on save, so don't flag them.
        unavailable: !pendingOnDutyCrewIds.has(crew.id),
      })),
      ...unavailableAssigned.map(({ crew }) => ({ crew, unavailable: true })),
    ]
  }, [availableCrewData, isEditMode, tripLegData?.crew, pendingOnDutyCrewIds])

  const hasUnavailableCrew = useMemo(() => {
    return mergedCrew.some((entry) => entry.unavailable && crewIds.includes(entry.crew.id))
  }, [mergedCrew, crewIds])

  // Per-leg acceptance state (TripLegCrew.acknowledged) for persisted
  // assignments — crew picked in the UI but not yet saved have no row here
  // and get no badge.
  const acknowledgedByCrewId = useMemo(
    () => new Map<string, boolean>((tripLegData?.crew ?? []).map((c) => [c.id, c.acknowledged])),
    [tripLegData?.crew],
  )

  const selectedCrewDetails = useMemo(() => {
    return mergedCrew.filter(({ crew }) => crewIds.includes(crew.id))
  }, [mergedCrew, crewIds])

  // Full crew list for the inline create-mode picker: available crew are selectable, all
  // others are shown with their precise duty status but cannot be selected.
  const crewForSelection = useMemo(() => {
    const available = (availableCrewData?.availableCrew ?? []).map((e) => ({
      crew: e.crew,
      dutyStatus: e.dutyStatus as string,
      selectable: true,
      reason: undefined as 'no_roster' | 'flight_conflict' | undefined,
      otherAircraftRegistration: null as string | null,
    }))
    const unavailable = (availableCrewData?.unavailableCrew ?? []).map((e) => ({
      crew: e.crew,
      dutyStatus: (e.dutyStatus ?? 'OffDuty') as string,
      selectable: false,
      reason: e.reason as 'no_roster' | 'flight_conflict' | undefined,
      otherAircraftRegistration: e.otherAircraftRegistration ?? null,
    }))
    return [...available, ...unavailable]
  }, [availableCrewData])

  const filteredCrewForSelection = useMemo(() => {
    const q = crewSearch.trim().toLowerCase()
    if (!q) return crewForSelection
    return crewForSelection.filter(
      ({ crew }) =>
        crew.name?.toLowerCase().includes(q) ||
        crew.firstName?.toLowerCase().includes(q) ||
        crew.lastName?.toLowerCase().includes(q) ||
        crew.crewRole?.toLowerCase().includes(q) ||
        formatCrewRole(crew.crewRole).toLowerCase().includes(q)
    )
  }, [crewForSelection, crewSearch])

  // All passengers for inline selection (Step 3)
  const { data: allPassengers = [], isPending: isAllPassengersLoading } =
    usePassengerGetAll()

  const selectedPaxDetails = useMemo(() => {
    return allPassengers.filter((p) => paxIds.includes(p.id))
  }, [allPassengers, paxIds])

  const filteredPaxForSelection = useMemo(() => {
    const q = paxSearch.trim().toLowerCase()
    if (!q) return allPassengers
    return allPassengers.filter(
      (p) =>
        p.firstName?.toLowerCase().includes(q) ||
        p.lastName?.toLowerCase().includes(q) ||
        (p.role && p.role.toLowerCase().includes(q))
    )
  }, [allPassengers, paxSearch])

  // Timezone-aware validation: arrival must be after departure in actual UTC
  const hasArrivalBeforeDeparture = useMemo(() => {
    if (!isOpen) return false
    if (!mergedDepartureDateTime || !mergedArrivalDateTime) return false
    const result = !dayjs(mergedArrivalDateTime).isAfter(mergedDepartureDateTime)
    // console.log('[FlightDrawer] arrival validation:', {
    //   mergedDepartureDateTime: mergedDepartureDateTime?.toISOString(),
    //   mergedArrivalDateTime: mergedArrivalDateTime?.toISOString(),
    //   hasArrivalBeforeDeparture: result,
    //   departureAirportTz: departureAirport?.timezone,
    //   arrivalAirportTz: arrivalAirport?.timezone,
    // })
    return result
  }, [isOpen, mergedDepartureDateTime, mergedArrivalDateTime, departureAirport?.timezone, arrivalAirport?.timezone])

  // Reset step when drawer opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1)
      userTouchedScheduleRef.current = false
      setCrewSearch('')
      setPaxSearch('')
      setPendingOnDutyCrewIds(new Set())
    }
    setOpenRowId(null)
  }, [isOpen])

  // Auto-estimate arrival from great-circle distance + taxi/climb/descent buffer
  // once the user has touched any schedule input. Skipped on initial edit-mode
  // load so server-saved arrivals aren't clobbered.
  useEffect(() => {
    if (!userTouchedScheduleRef.current) return
    if (!departureAirport || !arrivalAirport) return
    if (!mergedDepartureDateTime) return

    const arrivalUtc = estimateArrivalDateTime(
      departureAirport,
      arrivalAirport,
      mergedDepartureDateTime
    )
    const arrLocal = dayjs.utc(arrivalUtc).tz(arrivalAirport.timezone)
    const newArrivalDate = dayjs(arrLocal.format('YYYY-MM-DD')).startOf('day').toDate()
    const newArrivalTime = dayjs()
      .hour(arrLocal.hour())
      .minute(arrLocal.minute())
      .second(0)
      .millisecond(0)
      .toDate()

    setValue('arrivalDate', newArrivalDate, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
    setValue('arrivalTime', newArrivalTime, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
  }, [departureAirport, arrivalAirport, mergedDepartureDateTime, setValue])

  // Close any revealed swipe row when switching steps.
  useEffect(() => {
    setOpenRowId(null)
  }, [currentStep])

  const resetForm = () => {
    reset({
      aircraftId: '',
      arrivalAirportIcao: '',
      arrivalDate: null,
      arrivalTime: null,
      departureAirportIcao: '',
      departureDate: null,
      departureTime: null,
      paxIds: [],
      unknownPaxCount: 0,
      crewIds: [],
      departureFboId: null,
      arrivalFboId: null,
    })
    setCurrentStep(1)
    setSelectedTemplateIds(originalTemplateIds)
    setRemovedOrphanTaskIds([])
    setPendingOnDutyCrewIds(new Set())
    setStagedDocuments([])
    setStagedExistingDocIds([])
    setRemoveDocTarget(null)
    setViewDocumentId(null)
    setIsViewDocumentOpen(false)
    setIsAddDocumentChooserOpen(false)
    setIsUploadDocumentOpen(false)
    setIsAttachDocumentsOpen(false)
  }

  // Edit-mode remove: replace-all with the remaining ids (same pattern as TripDetailView)
  const legDocuments = tripLegData?.documents ?? []
  const confirmRemoveDocument = async () => {
    if (!removeDocTarget || !tripLegId) return
    try {
      const remaining = legDocuments
        .filter((d) => d.id !== removeDocTarget.id)
        .map((d) => d.id)
      await updateLegDocuments.mutateAsync({ tripLegId, documentIds: remaining })
      toast.success('Document removed from flight')
      setRemoveDocTarget(null)
    } catch (err: any) {
      toast.error(err?.message || 'Failed to remove document')
    }
  }

  const onSubmit = async (_data: TripLegFormData) => {
    try {
      // Merge date and time fields into DateTime using airport timezones
      // The user picks a local time at the airport, so we interpret it in the airport's IANA timezone
      const depTz = departureAirport!.timezone
      const arrTz = arrivalAirport!.timezone

      const departureDateTime = dayjs.tz(
        `${dayjs(departureDate).format('YYYY-MM-DD')} ${dayjs(departureTime).format('HH:mm')}`,
        depTz
      ).toDate()

      const arrivalDateTime = dayjs.tz(
        `${dayjs(arrivalDate).format('YYYY-MM-DD')} ${dayjs(arrivalTime).format('HH:mm')}`,
        arrTz
      ).toDate()

      // Send departure date as YYYY-MM-DD string to avoid timezone issues in roster lookups
      const departureDateStr = dayjs(departureDate).format('YYYY-MM-DD')

      // Put any admin-overridden off-duty / other-aircraft crew on duty for this flight's aircraft
      // before saving the leg, so they pass the server-side roster-eligibility check. The roster
      // upsert creates (off-duty) or moves (other aircraft) the entry. Intersect with the final
      // selection so overrides the admin later removed aren't written.
      const onDutyToCreate = crewIds.filter((id) => pendingOnDutyCrewIds.has(id))
      for (const crewId of onDutyToCreate) {
        await assignOnDuty(crewId)
      }

      if (isEditMode && tripLegId) {
        // Update trip leg with crew (skip if nothing changed)
        if (isDirty) {
          await updateTripLeg.mutateAsync({
            aircraftId: watchedAircraftId,
            arrivalAirportIcao,
            arrivalDateTime,
            departureAirportIcao,
            departureDateTime,
            departureDateStr,
            id: tripLegId,
            passengerIds: paxIds,
            unknownPaxCount,
            crewIds,
            departureFboId,
            arrivalFboId,
          })
        }

        // Diff the desired template selection against what's currently attached
        // and issue attach/detach mutations accordingly. Orphan tasks (no template)
        // can only leave via the swipe-to-delete fallback.
        const originalSet = new Set(originalTemplateIds)
        const selectedSet = new Set(selectedTemplateIds)
        const toAttachTemplateIds = selectedTemplateIds.filter((id) => !originalSet.has(id))
        const toDetachTaskIds = [
          ...tasks
            .filter(
              (t) => t.checklistTemplateId && !selectedSet.has(t.checklistTemplateId)
            )
            .map((t) => t.id),
          ...removedOrphanTaskIds,
        ]

        for (const id of toDetachTaskIds) {
          await detachTask.mutateAsync({ id })
        }
        for (const templateId of toAttachTemplateIds) {
          await attachTask.mutateAsync({
            checklistTemplateId: templateId,
            tripLegId,
          })
        }

        toast.success('Changes saved')
        // Keep the drawer open. Reset the dirty baseline to the just-saved
        // values and clear the queued task removals so Save disables again.
        reset({
          aircraftId: watchedAircraftId,
          arrivalAirportIcao,
          arrivalDate,
          arrivalTime,
          departureAirportIcao,
          departureDate,
          departureTime,
          paxIds,
          unknownPaxCount,
          crewIds,
          departureFboId,
          arrivalFboId,
        })
        setRemovedOrphanTaskIds([])
        setPendingOnDutyCrewIds(new Set())
      } else {
        // Create new trip leg
        const newLeg = await addTripLeg.mutateAsync({
          tripId,
          aircraftId: watchedAircraftId,
          arrivalAirportIcao,
          arrivalDateTime,
          departureAirportIcao,
          departureDateTime,
          departureDateStr,
          passengerIds: paxIds,
          unknownPaxCount,
          crewIds,
          departureFboId,
          arrivalFboId,
        })

        // Attach the templates picked in the Tasks step as tasks on the new leg.
        try {
          for (const templateId of selectedTemplateIds) {
            await attachTask.mutateAsync({
              checklistTemplateId: templateId,
              tripLegId: newLeg.id,
            })
          }
        } catch (e) {
          // The leg is already created; surface a non-fatal warning and still
          // close so the user doesn't resubmit and create a duplicate leg.
          console.error('Failed to attach some tasks:', e)
          toast.error('Flight created, but some tasks could not be attached.')
        }

        // Upload the staged documents and link them (plus any staged existing
        // docs) to the new leg. Non-fatal for the same reason as tasks above.
        try {
          const uploadedIds: string[] = []
          for (const staged of stagedDocuments) {
            const s3Key = generateComplianceS3Key(
              staged.subType ?? DocumentSubType.Manual,
              staged.file.name
            )
            await uploadFileWithPresignedPost(staged.file, s3Key, getUploadPost.mutateAsync)
            const doc = await createDocument.mutateAsync({
              title: staged.title,
              version: '1',
              category: 'Compliance',
              subType: staged.subType ?? DocumentSubType.Manual,
              documentNumber: staged.documentNumber,
              issuer: staged.issuer,
              issueDate: staged.issueDate ?? undefined,
              expiryDate: staged.expiryDate ?? undefined,
              isPublic: staged.isPublic,
              s3Key,
              fileName: staged.file.name,
            })
            uploadedIds.push(doc.id)
          }
          if (uploadedIds.length > 0 || stagedExistingDocIds.length > 0) {
            await updateLegDocuments.mutateAsync({
              tripLegId: newLeg.id,
              documentIds: [...stagedExistingDocIds, ...uploadedIds],
            })
          }
        } catch (e) {
          console.error('Failed to attach some documents:', e)
          toast.error('Flight created, but some documents could not be uploaded.')
        }

        track(EVENTS.TRIP_LEG_ADDED, { tripId })
        if (crewIds.length > 0) {
          track(EVENTS.CREW_ASSIGNED_TO_LEG, { tripId, crewCount: crewIds.length })
        }

        toast.success('Flight added')
        // Close the drawer after a successful create.
        resetForm()
        onClose()
      }
    } catch (error: any) {
      console.error('Failed to save trip leg:', error)

      // Extract error message from the API response
      const errorMessage =
        error?.message || 'Failed to save trip leg. Please try again.'
      toast.error(errorMessage)
    }
  }

  const handleCancel = () => {
    resetForm()
    onClose()
  }

  const handleRemoveFlight = async () => {
    if (!tripLegId) return

    try {
      await deleteTripLeg.mutateAsync({ id: tripLegId })
      toast.success(isDraft ? 'Flight removed' : 'Flight cancelled successfully')
      setIsDeleteDialogOpen(false)
      resetForm()
      onClose()
    } catch (error: any) {
      console.error('Failed to delete trip leg:', error)
      const errorMessage =
        error?.message || 'Failed to delete flight. Please try again.'
      toast.error(errorMessage)
    }
  }

  // Step 1 flight fields validation
  const isStep1Valid =
    isOpen &&
    !!watchedAircraftId &&
    !!departureAirportIcao &&
    !!departureDate &&
    !!departureTime &&
    !!arrivalAirportIcao &&
    !!arrivalDate &&
    !!arrivalTime &&
    !hasOverlap &&
    !hasArrivalBeforeDeparture &&
    !maintenanceConflict &&
    !errors.departureAirportIcao &&
    !errors.arrivalAirportIcao

  if (isOpen) {
    // console.log('[FlightDrawer] step1 validation:', {
    //   departureAirportIcao: !!departureAirportIcao,
    //   departureDate: departureDate,
    //   departureTime: departureTime,
    //   arrivalAirportIcao: !!arrivalAirportIcao,
    //   arrivalDate: arrivalDate,
    //   arrivalTime: arrivalTime,
    //   hasOverlap,
    //   hasArrivalBeforeDeparture,
    //   errors: Object.keys(errors),
    //   isStep1Valid,
    //   mergedDep: mergedDepartureDateTime?.toISOString(),
    //   mergedArr: mergedArrivalDateTime?.toISOString(),
    //   depTz: departureAirport?.timezone,
    //   arrTz: arrivalAirport?.timezone,
    // })
  }

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep((prev) => (prev + 1) as Step)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => (prev - 1) as Step)
    }
  }

  // While a save is in flight the form already passed validation, so any error
  // that surfaces is a transient artifact — the overlap check refetches against
  // the just-created leg (create mode has no excludeLegId) and the post-save
  // reset clears the fields — not something the user should see. Suppress the
  // step badges until the save settles to avoid a flashing red triangle.
  const isSaving =
    isSubmitting || addTripLeg.isPending || attachTask.isPending

  // Compute per-step error flags
  const stepErrors: Record<Step, boolean> = isSaving
    ? { 1: false, 2: false, 3: false, 4: false, 5: false }
    : {
        1:
          !!errors.aircraftId ||
          !!errors.departureAirportIcao ||
          !!errors.departureDate ||
          !!errors.departureTime ||
          !!errors.arrivalAirportIcao ||
          !!errors.arrivalDate ||
          hasOverlap ||
          hasArrivalBeforeDeparture ||
          !!maintenanceConflict,
        2: false,
        3: !!errors.paxIds || !!errors.unknownPaxCount,
        4: false,
        5: false,
      }

  const handleUnselectCrew = (crewId: string) => {
    const newIds = crewIds.filter((id) => id !== crewId)
    setValue('crewIds', newIds, {
      shouldValidate: true,
      shouldTouch: true,
      shouldDirty: true,
    })
  }

  const handleSelectCrew = (selectedIds: string[]) => {
    setValue('crewIds', selectedIds, {
      shouldValidate: true,
      shouldTouch: true,
      shouldDirty: true,
    })
  }

  const handleUnselectPax = (paxId: string) => {
    const newIds = paxIds.filter((id) => id !== paxId)
    setValue('paxIds', newIds, {
      shouldValidate: true,
      shouldTouch: true,
      shouldDirty: true,
    })
  }

  const handleSelectPax = (selectedIds: string[], unknownCount?: number) => {
    setValue('paxIds', selectedIds, {
      shouldValidate: true,
      shouldTouch: true,
      shouldDirty: true,
    })
    if (unknownCount !== undefined) {
      setUnknownPaxCount(unknownCount)
    }
  }

  const setUnknownPaxCount = (count: number) => {
    setValue('unknownPaxCount', Math.max(0, count), {
      shouldValidate: true,
      shouldTouch: true,
      shouldDirty: true,
    })
  }

  // Inline (create-mode) toggles: add/remove a single id from the form selection.
  const toggleCrew = (crewId: string) => {
    if (crewIds.includes(crewId)) {
      handleUnselectCrew(crewId)
    } else {
      handleSelectCrew([...crewIds, crewId])
    }
  }

  const togglePax = (paxId: string) => {
    if (paxIds.includes(paxId)) {
      handleUnselectPax(paxId)
    } else {
      handleSelectPax([...paxIds, paxId])
    }
  }

  // Departure date as a YYYY-MM-DD string, used for roster lookups/writes.
  const departureDateStr = departureDate ? dayjs(departureDate).format('YYYY-MM-DD') : undefined

  // Write an On-Duty roster entry for a crew member on the flight's date+aircraft. The roster
  // upsert (delete crew+date entry, recreate) both *creates* one for off-duty crew and *moves* an
  // existing one from another tail to this aircraft. Called at flight-save time, not on selection.
  const assignOnDuty = async (crewId: string) => {
    if (!departureDateStr || !watchedAircraftId) {
      throw new Error('Missing departure date or aircraft')
    }
    await updateRoster.mutateAsync({
      crewId,
      date: departureDateStr,
      aircraftId: watchedAircraftId,
      dutyStatus: 'OnDuty',
      // Never ping the crew member here. On a Draft trip they must hear
      // nothing at all; on a published one the leg save itself sends the
      // better-worded "New flight assigned" / "Flight updated".
      notify: false,
    })
  }

  // Inline list (create mode): confirm dialog only records the intent + selects the crew. The
  // roster entry is written when the flight is saved (see onSubmit).
  const handleConfirmAssignOnDuty = () => {
    if (!assignTarget) return
    const { id } = assignTarget
    handleSelectCrew([...crewIds, id])
    setPendingOnDutyCrewIds((prev) => new Set(prev).add(id))
    setAssignTarget(null)
  }

  const aircraftRegistration =
    selectedAircraft?.registration ?? tripLegData?.aircraft?.registration

  // Generate a Passenger Manifest PDF and hand it to the native share sheet
  // (file download on web). Passport details come from each passenger's
  // Passport document — non-admins only receive public ones, so missing data
  // simply prints as blank cells (the form is completed by hand either way).
  const handleShareManifest = async () => {
    hapticLight()
    setIsSharingManifest(true)
    try {
      const passports = await Promise.all(
        selectedPaxDetails.map(async (p) => {
          const docs = await orpcClient.document
            .getByPassenger({ passengerId: p.id })
            .catch(() => [])
          return docs.find((d) => d.subType === 'Passport') ?? null
        }),
      )
      await exportPassengerManifestPdf({
        companyName: companyData?.name ?? '',
        aircraftRegistration: aircraftRegistration ?? '',
        date: mergedDepartureDateTime ?? tripLegData?.departureDateTime ?? new Date(),
        departureIcao: departureAirportIcao || tripLegData?.departureAirportIcao || '',
        arrivalIcao: arrivalAirportIcao || tripLegData?.arrivalAirportIcao || '',
        passengers: selectedPaxDetails.map((p, i) => {
          const passport = passports[i]
          return {
            name: formatManifestName(p.firstName, p.lastName),
            // Date-only values — format in UTC so negative offsets don't shift a day
            dateOfBirth: p.dateOfBirth ? dayjs.utc(p.dateOfBirth).format('DD-MMM-YYYY') : '',
            passportNo: passport?.documentNumber ?? '',
            passportExpiry: passport?.expiryDate
              ? dayjs.utc(passport.expiryDate).format('DD-MMM-YYYY')
              : '',
            nationality: p.nationality ?? '',
          }
        }),
        unknownPaxCount,
        crewCount: crewIds.length,
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to share manifest'
      // Dismissing the native share sheet rejects with a cancel error — not a failure
      if (!message.toLowerCase().includes('cancel')) {
        toast.error(message)
      }
    } finally {
      setIsSharingManifest(false)
    }
  }

  const titleNode =
    isEditMode && (!tripLegData || !departureAirport || !arrivalAirport)
      ? 'Loading...'
      : departureAirport?.icaoCode && arrivalAirport?.icaoCode
        ? [`${departureAirport.icaoCode} → ${arrivalAirport.icaoCode}`, aircraftRegistration]
            .filter(Boolean)
            .join(' · ')
        : 'Create Trip Leg'

  const headerActions = (
    <div className="flex items-center gap-3">
      {isEditMode && tripLegId && onToggleMap && !isDesktop && (
        <Button
          variant="secondary"
          size="icon"
          roundness="round"
          className="size-8"
          onClick={onToggleMap}
        >
          <Globe className="w-4 h-4" />
        </Button>
      )}
      {isEditMode && !readOnly && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" size="icon" roundness="round" className="size-8">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem variant="destructive" onClick={() => setIsDeleteDialogOpen(true)}>
              <Trash2 className="w-4 h-4" />
              {removeFlightLabel}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <Button variant="secondary" size="icon" roundness="round" className="size-8" onClick={handleCancel}>
        <X className="w-4 h-4" />
      </Button>
    </div>
  )

  const summaryNode = !isCreateMode ? (
    <TripLegSummary
      tasksCount={visibleTaskCount}
      crewCount={crewIds.length}
      paxCount={paxIds.length + unknownPaxCount}
    />
  ) : null

  const stepIndicatorNode = isCreateMode ? (
    <div className="shrink-0">
      <Stepper
        steps={([1, 2, 3, 4, 5] as Step[]).map((step) => ({
          label: STEP_LABELS[step],
          hasError: stepErrors[step],
        }))}
        currentStep={currentStep}
        onStepClick={(step) => setCurrentStep(step as Step)}
      />
    </div>
  ) : null

  // Passengers belong in the gate too: paxIds comes from the leg, but the
  // passenger list is a separate query, so without this the PAX section
  // renders "No passengers assigned" for a flight that has some.
  const contentHidden =
    isEditMode && (isTripLegLoading || isAvailableCrewLoading || isAllPassengersLoading)

  // Skeleton placeholders shown in place of the form while edit-mode data loads.
  const skeletonContent = (
    <div className="flex flex-col gap-2">
      {/* PAX onboarding card */}
      <Skeleton className="mt-4 h-[62px] w-full rounded-2xl" />

      {/* Departure */}
      <SectionTitle label="Departure" className="pb-2 pt-4" />
      <Skeleton className="h-12 w-full rounded-xl" />
      <div className="flex gap-3">
        <Skeleton className="h-12 flex-1 rounded-xl" />
        <Skeleton className="h-12 flex-1 rounded-xl" />
      </div>

      {/* Arrival */}
      <SectionTitle label="Arrival" className="py-2" />
      <Skeleton className="h-12 w-full rounded-xl" />
      <div className="flex gap-3">
        <Skeleton className="h-12 flex-1 rounded-xl" />
        <Skeleton className="h-12 flex-1 rounded-xl" />
      </div>

      {/* Crew */}
      <SectionTitle label="Crew" className="py-2" />
      <Skeleton className="h-20 w-full rounded-2xl" />

      {/* PAX */}
      <SectionTitle label="PAX" className="py-2" />
      <Skeleton className="h-20 w-full rounded-2xl" />

      {/* Tasks */}
      <SectionTitle label="Tasks" className="pt-6 pb-2" />
      <Skeleton className="h-20 w-full rounded-2xl" />
    </div>
  )

  const scrollContent = (
    <>
            {/* Pilot CTA: flight log status at the top of the scroll area.
                Hidden on drafts and on legs that haven't departed yet — the
                log is filled in after the flight. Only pilots crewed on the
                leg may start a log; an existing log stays visible (read-only)
                to every pilot. */}
            {isPilot &&
              tripLegId &&
              !isDraft &&
              ((canLogLeg && isPilotOnLeg) || !!flightLog) && (
              <FlightLogStatusCard
                lifecycle={deriveFlightLogLifecycle(flightLog)}
                routeTimes={
                  (flightLog?.routeTimes ?? null) as FlightLogCardRouteTimes | null
                }
                postFlight={
                  (flightLog?.signOff as { postFlight?: FlightLogCardPostFlight | null } | null)
                    ?.postFlight ?? null
                }
                onAction={() => setIsFlightLogOpen(true)}
                className="mt-4"
              />
            )}

            {/* PAX onboarding status — persisted legs on published trips only
                (boarding is meaningless on drafts). Not gated on readOnly:
                crew mark boarding from the read-only schedule drawer. */}
            {isEditMode && tripLegId && tripLegData && tripData && !isDraft && (
              <div className="pt-4">
                <PaxOnboardingCard
                  tripLegId={tripLegId}
                  boarded={!!tripLegData.paxBoarded}
                  boardedAt={tripLegData.paxBoardedAt}
                  paxCount={paxIds.length + unknownPaxCount}
                />
              </div>
            )}

            {/* Step 1: Flight */}
            {(isEditMode || currentStep === 1) && (
              <div className="flex flex-col gap-4 pt-4">
                {/* Aircraft (per-flight) */}
                <PrntrCard title="Aircraft">
                  <PickerRowField
                    control={control}
                    name="aircraftId"
                    label="Tail Number"
                    iconLeft={<Plane />}
                    display={(value) => (value ? selectedAircraft?.registration ?? '—' : null)}
                    onOpen={() => setIsAircraftPickerOpen(true)}
                    hideChevron
                    readOnly={readOnly || isCompleted}
                    error={errors.aircraftId?.message}
                  />
                </PrntrCard>

                {/* Departure */}
                <PrntrCard title="Departure">
                  <PickerRowField
                    control={control}
                    name="departureAirportIcao"
                    label="Airport"
                    iconLeft={<PlaneTakeoff />}
                    labelPill={
                      departureAirportIcao ? (
                        <AirportInfoPill onClick={() => setInfoIcao(departureAirportIcao)} />
                      ) : undefined
                    }
                    display={(value) =>
                      value ? departureAirport?.icaoCode ?? (value as string) : null
                    }
                    onOpen={() => setIsDepartureAirportPickerOpen(true)}
                    hideChevron
                    readOnly={readOnly || isCompleted}
                    error={errors.departureAirportIcao?.message}
                  />
                  <DateObjectRow
                    control={control}
                    name="departureDate"
                    label="Date"
                    iconLeft={<Calendar />}
                    title="Departure Date"
                    readOnly={readOnly || isCompleted}
                    error={errors.departureDate?.message}
                    onChanged={() => {
                      userTouchedScheduleRef.current = true
                    }}
                  />
                  {/* Departure time — airport-local ⇄ UTC, editing either syncs the other */}
                  <PrntrInput
                    label="Time"
                    iconLeft={<Clock />}
                    suffix={
                      departureAirport
                        ? formatUtcOffsetLabel(
                            mergedDepartureDateTime ?? new Date(),
                            departureAirport.timezone
                          )
                        : undefined
                    }
                    empty={!departureTime}
                    error={errors.departureTime?.message}
                  >
                    {readOnly || isCompleted ? (
                      <PrntrValue
                        value={departureTime ? dayjs(departureTime).format('HH:mm') : null}
                        placeholder="—"
                      />
                    ) : (
                      <TimeInput
                        value={departureTime}
                        onChange={(date) => {
                          userTouchedScheduleRef.current = true
                          setValue('departureTime', date as Date, {
                            shouldValidate: true,
                            shouldTouch: true,
                            shouldDirty: true,
                          })
                        }}
                        hideClockIcon
                        className={bareTimeInputClass}
                      />
                    )}
                  </PrntrInput>
                  <PrntrInput
                    label="Time (UTC)"
                    iconLeft={<Globe />}
                    suffix="UTC"
                    empty={!departureUtcDisplay}
                  >
                    {readOnly || isCompleted ? (
                      <PrntrValue
                        value={
                          departureUtcDisplay ? dayjs(departureUtcDisplay).format('HH:mm') : null
                        }
                        placeholder="—"
                      />
                    ) : (
                      <TimeInput
                        value={departureUtcDisplay}
                        onChange={(date) => {
                          if (!date) return
                          userTouchedScheduleRef.current = true
                          applyUtcTimeEdit(
                            date,
                            mergedDepartureDateTime,
                            departureAirport?.timezone,
                            'departureDate',
                            'departureTime'
                          )
                        }}
                        hideClockIcon
                        className={bareTimeInputClass}
                      />
                    )}
                  </PrntrInput>

                  {/* Departure FBO / handling agent */}
                  <FboSection
                    kind="departure"
                    icao={departureAirportIcao || undefined}
                    value={departureFboId}
                    onChange={(id) =>
                      setValue('departureFboId', id, { shouldDirty: true, shouldTouch: true })
                    }
                    readOnly={readOnly || isCompleted}
                    savedFbo={tripLegData?.departureFbo ?? null}
                    allowAutoSelect={
                      !isEditMode || departureAirportIcao !== tripLegData?.departureAirportIcao
                    }
                  />
                </PrntrCard>

                {overlapMessage && (
                  <div className="flex items-start gap-2 rounded-lg bg-background-notice-subtle px-3 py-2">
                    <TriangleAlert className="size-4 text-background-notice-bold shrink-0 mt-0.5" />
                    <p className="text-sm text-background-notice-bold">
                      {overlapMessage}
                    </p>
                  </div>
                )}

                {maintenanceMessage && (
                  <div className="flex items-start gap-2 rounded-lg bg-background-notice-subtle px-3 py-2">
                    <TriangleAlert className="size-4 text-background-notice-bold shrink-0 mt-0.5" />
                    <p className="text-sm text-background-notice-bold">
                      {maintenanceMessage}
                    </p>
                  </div>
                )}

                {/* Arrival */}
                <PrntrCard title="Arrival">
                  <PickerRowField
                    control={control}
                    name="arrivalAirportIcao"
                    label="Airport"
                    iconLeft={<PlaneLanding />}
                    labelPill={
                      arrivalAirportIcao ? (
                        <AirportInfoPill onClick={() => setInfoIcao(arrivalAirportIcao)} />
                      ) : undefined
                    }
                    display={(value) =>
                      value ? arrivalAirport?.icaoCode ?? (value as string) : null
                    }
                    onOpen={() => setIsArrivalAirportPickerOpen(true)}
                    hideChevron
                    readOnly={readOnly || isCompleted}
                    error={errors.arrivalAirportIcao?.message}
                  />
                  <DateObjectRow
                    control={control}
                    name="arrivalDate"
                    label="Date"
                    iconLeft={<Calendar />}
                    title="Arrival Date"
                    readOnly={readOnly || isCompleted}
                    error={errors.arrivalDate?.message}
                  />
                  {/* Arrival time — airport-local ⇄ UTC, editing either syncs the other */}
                  <PrntrInput
                    label="Time"
                    iconLeft={<Clock />}
                    suffix={
                      arrivalAirport
                        ? formatUtcOffsetLabel(
                            mergedArrivalDateTime ?? new Date(),
                            arrivalAirport.timezone
                          )
                        : undefined
                    }
                    empty={!arrivalTime}
                    error={
                      errors.arrivalTime?.message ||
                      (hasArrivalBeforeDeparture ? 'Arrival must be after departure' : undefined)
                    }
                  >
                    {readOnly || isCompleted ? (
                      <PrntrValue
                        value={arrivalTime ? dayjs(arrivalTime).format('HH:mm') : null}
                        placeholder="—"
                      />
                    ) : (
                      <TimeInput
                        value={arrivalTime}
                        onChange={(date) => {
                          setValue('arrivalTime', date as Date, {
                            shouldValidate: true,
                            shouldTouch: true,
                            shouldDirty: true,
                          })
                        }}
                        hideClockIcon
                        className={bareTimeInputClass}
                      />
                    )}
                  </PrntrInput>
                  <PrntrInput
                    label="Time (UTC)"
                    iconLeft={<Globe />}
                    suffix="UTC"
                    empty={!arrivalUtcDisplay}
                  >
                    {readOnly || isCompleted ? (
                      <PrntrValue
                        value={arrivalUtcDisplay ? dayjs(arrivalUtcDisplay).format('HH:mm') : null}
                        placeholder="—"
                      />
                    ) : (
                      <TimeInput
                        value={arrivalUtcDisplay}
                        onChange={(date) =>
                          applyUtcTimeEdit(
                            date,
                            mergedArrivalDateTime,
                            arrivalAirport?.timezone,
                            'arrivalDate',
                            'arrivalTime'
                          )
                        }
                        hideClockIcon
                        className={bareTimeInputClass}
                      />
                    )}
                  </PrntrInput>

                  {/* Arrival FBO / handling agent */}
                  <FboSection
                    kind="arrival"
                    icao={arrivalAirportIcao || undefined}
                    value={arrivalFboId}
                    onChange={(id) =>
                      setValue('arrivalFboId', id, { shouldDirty: true, shouldTouch: true })
                    }
                    readOnly={readOnly || isCompleted}
                    savedFbo={tripLegData?.arrivalFbo ?? null}
                    allowAutoSelect={
                      !isEditMode || arrivalAirportIcao !== tripLegData?.arrivalAirportIcao
                    }
                  />
                </PrntrCard>
              </div>
            )}

            {/* Step 2: Crew — edit mode keeps the selected-list + picker drawer */}
            {isEditMode && (
              <div className={cn("flex flex-col gap-2", isEditMode && "pt-4")}>
                <SectionTitle
                  label="Crew"
                  count={crewIds.length}
                  icon={<IdCardLanyard className="size-5 text-content-brand-bold" />}
                  onAdd={
                    readOnly
                      ? undefined
                      : () => {
                          hapticLight()
                          setIsCrewPickerOpen(true)
                        }
                  }
                  addDisabled={!departureDate}
                  className="py-2"
                />

                {/* Selected Crew List */}
                {!departureDate ? (
                  <Card className="flex flex-col">
                    <div className="flex flex-col items-center justify-center py-12 px-4">
                      <AlertCircle className="size-8 text-content-primary-subtle mb-3" />
                      <p className="text-sm text-content-primary-subtle text-center">
                        Set a departure date to add crew.
                      </p>
                    </div>
                  </Card>
                ) : selectedCrewDetails.length === 0 ? (
                  <EmptyStateCard
                    label={readOnly ? 'No crew assigned' : 'Add crew members'}
                    onAdd={
                      readOnly
                        ? undefined
                        : () => {
                            hapticLight()
                            setIsCrewPickerOpen(true)
                          }
                    }
                  />
                ) : (
                <Card className="flex flex-col gap-2 overflow-hidden p-3">
                    {selectedCrewDetails.map(({ crew, unavailable }, index) => {
                        const initials = getInitials(crew.firstName, crew.lastName)
                        const displayName =
                          crew.firstName && crew.lastName
                            ? `${crew.firstName} ${crew.lastName}`
                            : crew.name
                        const rowId = `crew-${crew.id}`
                        return (
                          <Fragment key={crew.id}>
                            {index > 0 && <hr className="border-border-L1" />}
                            <SwipeableRow
                              isOpen={openRowId === rowId}
                              onOpenChange={(o) => setOpenRowId(o ? rowId : null)}
                              onDelete={() => {
                                handleUnselectCrew(crew.id)
                                setOpenRowId(null)
                              }}
                              variant="icon"
                              actionClassName="bg-content-primary-subtle"
                              actionIcon={<CircleMinus />}
                              disabled={readOnly}
                            >
                              <MenuItem
                                size="large"
                                type="button"
                                onClick={() => setCrewDetailId(crew.id)}
                                className="px-1"
                                leftIcon={
                                  <Avatar className="size-8 shrink-0">
                                    <AvatarFallback
                                      className="text-background"
                                      style={crew.avatarColor ? { backgroundColor: crew.avatarColor } : undefined}
                                    >
                                      {initials}
                                    </AvatarFallback>
                                  </Avatar>
                                }
                                secondaryText={formatCrewRole(crew.crewRole)}
                                rightIcon={
                                  acknowledgedByCrewId.has(crew.id) || unavailable ? (
                                    <div className="flex items-center gap-1">
                                      {acknowledgedByCrewId.has(crew.id) && (
                                        <CrewAcceptanceBadge accepted={acknowledgedByCrewId.get(crew.id)!} />
                                      )}
                                      {unavailable && <AlertBadge variant="error" />}
                                    </div>
                                  ) : undefined
                                }
                              >
                                {displayName}
                              </MenuItem>
                            </SwipeableRow>
                          </Fragment>
                        )
                      })}
                  </Card>
                )}

                {hasUnavailableCrew && (
                  <div className="flex items-start gap-2 rounded-lg bg-background-notice-subtle text-background-notice-bold px-3 py-2">
                    <TriangleAlert className="size-4 shrink-0 mt-0.5" />
                    <p className="text-sm">
                      Some assigned crew are unavailable for the selected departure time. Remove them to save.
                    </p>
                  </div>
                )}

              </div>
            )}

            {/* Step 2: Crew — create mode renders the full crew list inline */}
            {isCreateMode && currentStep === 2 && (
              <div className="flex flex-col gap-2">
                {!departureDate ? (
                  <Card className="flex flex-col">
                    <div className="flex flex-col items-center justify-center py-12 px-4">
                      <AlertCircle className="size-8 text-content-primary-subtle mb-3" />
                      <p className="text-sm text-content-primary-subtle text-center">
                        Set a departure date to add crew.
                      </p>
                    </div>
                  </Card>
                ) : (
                  <>
                    <SearchInput
                      value={crewSearch}
                      onChange={(e) => setCrewSearch(e.target.value)}
                      placeholder="Search crew"
                    />
                    <div
                      className="overflow-y-auto overscroll-contain max-h-[50vh]"
                      style={{ WebkitOverflowScrolling: 'touch' }}
                    >
                      {isAvailableCrewLoading ? (
                        <ListSkeleton />
                      ) : filteredCrewForSelection.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 px-4">
                          <p className="text-sm text-content-primary-subtle text-center">
                            {crewSearch
                              ? 'No crew members found matching your search'
                              : 'No crew available'}
                          </p>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-1">
                          {filteredCrewForSelection.map(({ crew, dutyStatus, selectable, reason, otherAircraftRegistration }) => {
                            const initials = getInitials(crew.firstName, crew.lastName)
                            const displayName =
                              crew.firstName && crew.lastName
                                ? `${crew.firstName} ${crew.lastName}`
                                : crew.name
                            const isSelected = crewIds.includes(crew.id)
                            // Crew already on another flight that day: informational only.
                            const isConflict = reason === 'flight_conflict'
                            // Admins can override an off-duty/unrostered crew (no_roster) to on duty.
                            const canOverride = isAdmin && !readOnly && reason === 'no_roster' && !isSelected
                            // Available crew toggle directly; an already-selected (now unavailable)
                            // crew stays removable.
                            const canToggle = selectable || isSelected
                            // Once selected via override, optimistically show On Duty (it'll be
                            // written on save) and hide the "other aircraft" tail label.
                            const isPendingOnDuty = pendingOnDutyCrewIds.has(crew.id)
                            const effectiveStatus = isSelected && isPendingOnDuty ? 'OnDuty' : dutyStatus
                            const showOtherTail = !!otherAircraftRegistration && !isSelected
                            const onRowClick = () => {
                              if (canToggle) toggleCrew(crew.id)
                              else if (canOverride)
                                setAssignTarget({ id: crew.id, name: displayName, otherAircraftRegistration })
                            }
                            const interactive = canToggle || canOverride
                            return (
                              <button
                                key={crew.id}
                                type="button"
                                disabled={!interactive}
                                onClick={onRowClick}
                                className={cn(
                                  'w-full flex items-center gap-3 min-h-9 px-3 py-2 rounded-md text-left transition-colors',
                                  !interactive
                                    ? 'opacity-50 pointer-events-none'
                                    : isSelected
                                      ? 'bg-surface-L2'
                                      : 'hover:bg-surface-L2/50'
                                )}
                              >
                                <Avatar className="size-8 shrink-0">
                                  <AvatarFallback
                                    className="text-background"
                                    style={crew.avatarColor ? { backgroundColor: crew.avatarColor } : undefined}
                                  >
                                    {initials}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex flex-1 min-w-0 flex-col items-start justify-center">
                                  <span className="text-sm leading-normal text-content-primary-bold truncate w-full">
                                    {displayName}
                                  </span>
                                  <span className="text-xs leading-normal text-content-primary-subtle truncate w-full">
                                    {formatCrewRole(crew.crewRole)}
                                  </span>
                                </div>
                                <div className="flex flex-col items-end gap-0.5 shrink-0">
                                  <DutyBadge status={effectiveStatus} />
                                  {showOtherTail && (
                                    <span className="text-[10px] leading-none text-content-primary-subtle">
                                      {otherAircraftRegistration}
                                    </span>
                                  )}
                                </div>
                                {isConflict ? (
                                  <Plane className="size-4 shrink-0 text-content-primary-subtle" />
                                ) : canToggle ? (
                                  isSelected ? (
                                    <CircleMinus className="size-5 shrink-0 text-content-primary-subtle" />
                                  ) : (
                                    <CirclePlus className="size-5 shrink-0 text-content-primary-subtle" />
                                  )
                                ) : canOverride ? (
                                  <CirclePlus className="size-5 shrink-0 text-content-primary-subtle" />
                                ) : null}
                              </button>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Step 3: PAX — edit mode keeps the selected-list + picker drawer */}
            {isEditMode && (
              <div className={cn("flex flex-col gap-2", isEditMode && "pt-4")}>
                <SectionTitle
                  label="PAX"
                  count={paxIds.length + unknownPaxCount}
                  icon={<User className="size-5 text-content-brand-bold" />}
                  // Not gated on readOnly: crew share manifests from the
                  // read-only schedule drawer too.
                  onShare={tripLegId ? handleShareManifest : undefined}
                  shareDisabled={isSharingManifest}
                  onAdd={
                    readOnly
                      ? undefined
                      : () => {
                          hapticLight()
                          setIsPaxPickerOpen(true)
                        }
                  }
                  className="py-2"
                />

                {/* Selected PAX List */}
                {selectedPaxDetails.length === 0 && unknownPaxCount === 0 ? (
                  <EmptyStateCard
                    label={readOnly ? 'No passengers assigned' : 'Add passengers'}
                    onAdd={
                      readOnly
                        ? undefined
                        : () => {
                            hapticLight()
                            setIsPaxPickerOpen(true)
                          }
                    }
                  />
                ) : (
                  <Card className="flex flex-col gap-2 overflow-hidden p-3">
                    {/* Unknown PAX — one row per unknown passenger, swipe to remove one */}
                    {Array.from({ length: unknownPaxCount }).map((_, index) => {
                      const rowId = `unknown-pax-${index}`
                      return (
                        <Fragment key={rowId}>
                          {index > 0 && <hr className="border-border-L1" />}
                          <SwipeableRow
                            isOpen={openRowId === rowId}
                            onOpenChange={(o) => setOpenRowId(o ? rowId : null)}
                            onDelete={() => {
                              setUnknownPaxCount(unknownPaxCount - 1)
                              setOpenRowId(null)
                            }}
                            variant="icon"
                            actionClassName="bg-content-primary-subtle"
                            actionIcon={<CircleMinus />}
                            disabled={readOnly}
                          >
                            <MenuItem
                              size="large"
                              type="button"
                              onClick={
                                readOnly
                                  ? undefined
                                  : () => {
                                      hapticLight()
                                      setIsPaxPickerOpen(true)
                                    }
                              }
                              className="px-1"
                              leftIcon={
                                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-content-primary-subtle-mid">
                                  <User className="size-5 text-content-primary-inverse" />
                                </div>
                              }
                              secondaryText="Guest"
                            >
                              Unknown
                            </MenuItem>
                          </SwipeableRow>
                        </Fragment>
                      )
                    })}
                    {selectedPaxDetails.map((passenger, index) => {
                        const initials = getInitials(passenger.firstName, passenger.lastName)
                        const displayName = `${passenger.firstName} ${passenger.lastName}`
                        const rowId = `pax-${passenger.id}`
                        return (
                          <Fragment key={passenger.id}>
                            {(index > 0 || unknownPaxCount > 0) && <hr className="border-border-L1" />}
                            <SwipeableRow
                              isOpen={openRowId === rowId}
                              onOpenChange={(o) => setOpenRowId(o ? rowId : null)}
                              onDelete={() => {
                                handleUnselectPax(passenger.id)
                                setOpenRowId(null)
                              }}
                              variant="icon"
                              actionClassName="bg-content-primary-subtle"
                              actionIcon={<CircleMinus />}
                              disabled={readOnly}
                            >
                              <MenuItem
                                size="large"
                                type="button"
                                onClick={() => setPaxDetailId(passenger.id)}
                                className="px-1"
                                leftIcon={
                                  <Avatar className="size-8 shrink-0">
                                    <AvatarFallback
                                      className="text-background"
                                      style={passenger.avatarColor ? { backgroundColor: passenger.avatarColor } : undefined}
                                    >
                                      {initials}
                                    </AvatarFallback>
                                  </Avatar>
                                }
                                secondaryText={passenger.role === 'Primary' ? 'Primary' : 'Guest'}
                              >
                                {displayName}
                              </MenuItem>
                            </SwipeableRow>
                          </Fragment>
                        )
                      })}
                  </Card>
                )}
              </div>
            )}

            {/* Step 3: PAX — create mode renders the full passenger list inline */}
            {isCreateMode && currentStep === 3 && (
              <div className="flex flex-col gap-2">
                <SearchInput
                  value={paxSearch}
                  onChange={(e) => setPaxSearch(e.target.value)}
                  placeholder="Search passengers"
                />
                <div className="flex items-center justify-between px-1">
                  <span className="text-xs font-semibold text-content-primary-subtle">
                    {paxIds.length + unknownPaxCount} selected
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      hapticLight()
                      setIsAddPaxOpen(true)
                    }}
                    className="flex items-center gap-1 p-1 text-xs font-semibold text-content-brand-bold active:opacity-70"
                  >
                    <Plus className="size-4" />
                    Add New
                  </button>
                </div>
                <UnknownPaxRow count={unknownPaxCount} onChange={setUnknownPaxCount} />
                <div
                  className="overflow-y-auto overscroll-contain max-h-[50vh]"
                  style={{ WebkitOverflowScrolling: 'touch' }}
                >
                  {isAllPassengersLoading ? (
                    <ListSkeleton />
                  ) : filteredPaxForSelection.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 px-4">
                      <p className="text-sm text-content-primary-subtle text-center">
                        {paxSearch
                          ? 'No passengers found matching your search'
                          : 'No passengers available'}
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1">
                      {filteredPaxForSelection.map((passenger) => {
                        const initials = getInitials(passenger.firstName, passenger.lastName)
                        const displayName =
                          passenger.firstName && passenger.lastName
                            ? `${passenger.firstName} ${passenger.lastName}`
                            : passenger.firstName || passenger.lastName || ''
                        const isSelected = paxIds.includes(passenger.id)
                        return (
                          <button
                            key={passenger.id}
                            type="button"
                            onClick={() => togglePax(passenger.id)}
                            className={cn(
                              'w-full flex items-center gap-3 min-h-9 px-3 py-2 rounded-md text-left transition-colors',
                              isSelected ? 'bg-surface-L2' : 'hover:bg-surface-L2/50'
                            )}
                          >
                            <Avatar className="size-8 shrink-0">
                              <AvatarFallback
                                className="text-background"
                                style={passenger.avatarColor ? { backgroundColor: passenger.avatarColor } : undefined}
                              >
                                {initials}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex flex-1 min-w-0 flex-col items-start justify-center">
                              <span className="text-sm leading-normal text-content-primary-bold truncate w-full">
                                {displayName}
                              </span>
                              <span className="text-xs leading-normal text-content-primary-subtle truncate w-full">
                                {passenger.role === 'Primary' ? 'Primary' : 'Guest'}
                              </span>
                            </div>
                            {isSelected ? (
                              <CircleMinus className="size-5 shrink-0 text-content-primary-subtle" />
                            ) : (
                              <CirclePlus className="size-5 shrink-0 text-content-primary-subtle" />
                            )}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tasks (admin edit mode, or create mode step 4) */}
            {((isEditMode && isAdmin && tripLegId) || (isCreateMode && currentStep === 4)) && (
              <div className={cn('flex flex-col gap-4', isEditMode ? 'pt-8' : 'pt-4')}>
                {(() => {
                  return (
                    <>
                      <SectionTitle
                        label="Tasks"
                        count={visibleTaskCount}
                        icon={<ClipboardCheck className="size-5 text-content-brand-bold" />}
                        onAdd={
                          readOnly
                            ? undefined
                            : () => {
                                hapticLight()
                                setIsTaskPickerOpen(true)
                              }
                        }
                        className="py-2"
                      />

                      {visibleTaskCount === 0 ? (
                        <EmptyStateCard
                          label={readOnly ? 'No tasks' : 'Add tasks'}
                          onAdd={
                            readOnly
                              ? undefined
                              : () => {
                                  hapticLight()
                                  setIsTaskPickerOpen(true)
                                }
                          }
                        />
                      ) : (
                        <Card className="flex flex-col gap-2 overflow-hidden p-3">
                          {selectedTemplateIds.map((templateId, index) => {
                              const attachedTask = tasks.find(
                                (t) => t.checklistTemplateId === templateId
                              )
                              if (attachedTask) {
                                const rowId = `task-${attachedTask.id}`
                                const completed = countRespondedItems(attachedTask.items)
                                return (
                                  <Fragment key={rowId}>
                                    {index > 0 && <hr className="border-border-L1" />}
                                    <SwipeableRow
                                      isOpen={openRowId === rowId}
                                      onOpenChange={(o) => setOpenRowId(o ? rowId : null)}
                                      onDelete={() => {
                                        setSelectedTemplateIds((prev) =>
                                          prev.filter((id) => id !== templateId)
                                        )
                                        setOpenRowId(null)
                                      }}
                                      variant="icon"
                                      actionClassName="bg-content-primary-subtle"
                                      actionIcon={<CircleMinus />}
                                      disabled={readOnly}
                                    >
                                      <MenuItem
                                        size="large"
                                        type="button"
                                        onClick={() => {
                                          onClose()
                                          navigate({
                                            to: '/task/$taskId',
                                            params: { taskId: attachedTask.id },
                                          })
                                        }}
                                        className="px-1"
                                        secondaryText={`${completed}/${attachedTask.items.length} Complete`}
                                        rightIcon={<TaskStatusBadge status={attachedTask.status} />}
                                      >
                                        {attachedTask.name}
                                      </MenuItem>
                                    </SwipeableRow>
                                  </Fragment>
                                )
                              }

                              const template = allTemplates.find((t) => t.id === templateId)
                              if (!template) return null
                              const rowId = `pending-${templateId}`
                              return (
                                <Fragment key={rowId}>
                                  {index > 0 && <hr className="border-border-L1" />}
                                  <SwipeableRow
                                    isOpen={openRowId === rowId}
                                    onOpenChange={(o) => setOpenRowId(o ? rowId : null)}
                                    onDelete={() => {
                                      setSelectedTemplateIds((prev) =>
                                        prev.filter((id) => id !== templateId)
                                      )
                                      setOpenRowId(null)
                                    }}
                                    variant="icon"
                                    actionClassName="bg-content-primary-subtle"
                                    actionIcon={<CircleMinus />}
                                    disabled={readOnly}
                                  >
                                    <MenuItem
                                      size="large"
                                      type="button"
                                      className="px-1"
                                      secondaryText={`0/${template.items.length} Complete`}
                                      rightIcon={<TaskStatusBadge status="Draft" />}
                                    >
                                      {template.name}
                                    </MenuItem>
                                  </SwipeableRow>
                                </Fragment>
                              )
                            })}
                            {orphanTasks.map((task, index) => {
                              const rowId = `orphan-${task.id}`
                              const completed = countRespondedItems(task.items)
                              return (
                                <Fragment key={rowId}>
                                  {(index > 0 || selectedTemplateIds.length > 0) && (
                                    <hr className="border-border-L1" />
                                  )}
                                  <SwipeableRow
                                    isOpen={openRowId === rowId}
                                    onOpenChange={(o) => setOpenRowId(o ? rowId : null)}
                                    onDelete={() => {
                                      setRemovedOrphanTaskIds((prev) => [...prev, task.id])
                                      setOpenRowId(null)
                                    }}
                                    variant="icon"
                                    actionClassName="bg-content-primary-subtle"
                                    actionIcon={<CircleMinus />}
                                    disabled={readOnly}
                                  >
                                    <MenuItem
                                      size="large"
                                      type="button"
                                      onClick={() => {
                                        onClose()
                                        navigate({
                                          to: '/task/$taskId',
                                          params: { taskId: task.id },
                                        })
                                      }}
                                      className="px-1"
                                      secondaryText={`${completed}/${task.items.length} Complete`}
                                      rightIcon={<TaskStatusBadge status={task.status} />}
                                    >
                                      {task.name}
                                    </MenuItem>
                                  </SwipeableRow>
                                </Fragment>
                              )
                            })}
                        </Card>
                      )}
                    </>
                  )
                })()}
              </div>
            )}

            {/* Documents (edit mode, or create mode step 5). Unlike Tasks, the
                list renders for non-admins too — crew read flight docs from the
                read-only drawer; add/remove stays gated below. */}
            {((isEditMode && tripLegId) || (isCreateMode && currentStep === 5)) && (
              <div className={cn('flex flex-col gap-4', isEditMode ? 'pt-8' : 'pt-4')}>
                <SectionTitle
                  label="Documents"
                  count={
                    isEditMode
                      ? legDocuments.length
                      : stagedDocuments.length + stagedExistingDocIds.length
                  }
                  icon={<FileText className="size-5 text-content-brand-bold" />}
                  onAdd={
                    readOnly || isCompleted
                      ? undefined
                      : () => {
                          hapticLight()
                          setIsAddDocumentChooserOpen(true)
                        }
                  }
                  className="py-2"
                />

                {isEditMode ? (
                  legDocuments.length === 0 ? (
                    <EmptyStateCard
                      label={readOnly || isCompleted ? 'No documents' : 'Add a document'}
                      onAdd={
                        readOnly || isCompleted
                          ? undefined
                          : () => {
                              hapticLight()
                              setIsAddDocumentChooserOpen(true)
                            }
                      }
                    />
                  ) : (
                    <Card className="flex flex-col gap-2 overflow-hidden p-3">
                      {legDocuments.map((doc, index) => {
                        const rowId = `legdoc-${doc.id}`
                        return (
                          <Fragment key={rowId}>
                            {index > 0 && <hr className="border-border-L1" />}
                            <SwipeableRow
                              isOpen={openRowId === rowId}
                              onOpenChange={(o) => setOpenRowId(o ? rowId : null)}
                              onDelete={() => {
                                setRemoveDocTarget({ id: doc.id, title: doc.title })
                                setOpenRowId(null)
                              }}
                              variant="icon"
                              actionLabel="Remove"
                              actionClassName="bg-content-primary-subtle"
                              actionIcon={<CircleMinus />}
                              disabled={readOnly || isCompleted}
                            >
                              <MenuItem
                                size="large"
                                type="button"
                                leftIcon={<File className="text-content-primary-subtle" />}
                                onClick={() => {
                                setViewDocumentId(doc.id)
                                setIsViewDocumentOpen(true)
                              }}
                                className="px-1"
                              >
                                {doc.title}
                              </MenuItem>
                            </SwipeableRow>
                          </Fragment>
                        )
                      })}
                    </Card>
                  )
                ) : stagedDocuments.length + stagedExistingDocIds.length === 0 ? (
                  <EmptyStateCard
                    label="Add a document"
                    onAdd={() => {
                      hapticLight()
                      setIsAddDocumentChooserOpen(true)
                    }}
                  />
                ) : (
                  <Card className="flex flex-col gap-2 overflow-hidden p-3">
                    {stagedDocuments.map((staged, index) => {
                      const rowId = `stageddoc-${index}`
                      return (
                        <Fragment key={rowId}>
                          {index > 0 && <hr className="border-border-L1" />}
                          <SwipeableRow
                            isOpen={openRowId === rowId}
                            onOpenChange={(o) => setOpenRowId(o ? rowId : null)}
                            onDelete={() => {
                              setStagedDocuments((prev) => prev.filter((_, i) => i !== index))
                              setOpenRowId(null)
                            }}
                            variant="icon"
                            actionLabel="Remove"
                            actionClassName="bg-content-primary-subtle"
                            actionIcon={<CircleMinus />}
                          >
                            <MenuItem
                              size="large"
                              type="button"
                              leftIcon={<File className="text-content-primary-subtle" />}
                              secondaryText={staged.file.name}
                              className="px-1"
                            >
                              {staged.title}
                            </MenuItem>
                          </SwipeableRow>
                        </Fragment>
                      )
                    })}
                    {stagedExistingDocs.map((doc, index) => {
                      const rowId = `stagedexisting-${doc.id}`
                      return (
                        <Fragment key={rowId}>
                          {(index > 0 || stagedDocuments.length > 0) && (
                            <hr className="border-border-L1" />
                          )}
                          <SwipeableRow
                            isOpen={openRowId === rowId}
                            onOpenChange={(o) => setOpenRowId(o ? rowId : null)}
                            onDelete={() => {
                              setStagedExistingDocIds((prev) => prev.filter((id) => id !== doc.id))
                              setOpenRowId(null)
                            }}
                            variant="icon"
                            actionLabel="Remove"
                            actionClassName="bg-content-primary-subtle"
                            actionIcon={<CircleMinus />}
                          >
                            <MenuItem
                              size="large"
                              type="button"
                              leftIcon={<File className="text-content-primary-subtle" />}
                              onClick={() => {
                                setViewDocumentId(doc.id)
                                setIsViewDocumentOpen(true)
                              }}
                              className="px-1"
                            >
                              {doc.title}
                            </MenuItem>
                          </SwipeableRow>
                        </Fragment>
                      )
                    })}
                  </Card>
                )}
              </div>
            )}

    </>
  )

  const footerHidden = isEditMode && !tripLegData

  const footerContent = (
            <div className="flex flex-col gap-2">
              {/* Edit mode: Save or Close */}
              {isEditMode && (
                <>
                  {!readOnly && (
                    <Button
                      variant='primary'
                      size="mini"
                      onClick={handleSubmit(onSubmit)}
                      className="w-full"
                      disabled={
                        (!isDirty && !hasTaskChanges) ||
                        isSubmitting ||
                        attachTask.isPending ||
                        detachTask.isPending ||
                        (isDirty && (!isValid || hasOverlap || isOverlapChecking || hasUnavailableCrew || hasArrivalBeforeDeparture || !!maintenanceConflict))
                      }
                    >
                      {isOverlapChecking ? 'Checking...' : isSubmitting || attachTask.isPending || detachTask.isPending ? 'Saving...' : 'Save Changes'}
                    </Button>
                  )}
                  {readOnly && (
                    <Button
                      variant="secondary"
                      size="mini"
                      onClick={handleCancel}
                      className="w-full"
                    >
                      Close
                    </Button>
                  )}
                </>
              )}

              {/* Create mode Step 1: Cancel + Next */}
              {isCreateMode && currentStep === 1 && (
                <div className="flex flex-row gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={handleCancel}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={handleNext}
                    disabled={!isStep1Valid}
                    className="flex-1"
                  >
                    Next
                  </Button>
                </div>
              )}

              {/* Create mode Steps 2-4: Back + Next */}
              {isCreateMode && currentStep >= 2 && currentStep <= 4 && (
                <div className="flex flex-row gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={handleBack}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={handleNext}
                    className="flex-1"
                  >
                    Next
                  </Button>
                </div>
              )}

              {/* Create mode Step 5: Back + Save */}
              {isCreateMode && currentStep === 5 && (
                <div className="flex flex-row gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={handleBack}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSubmit(onSubmit)}
                    disabled={!isDirty || !isValid || isSubmitting || attachTask.isPending || hasOverlap || isOverlapChecking || hasUnavailableCrew || hasArrivalBeforeDeparture || !!maintenanceConflict}
                    className="flex-1"
                  >
                    {isOverlapChecking ? 'Checking...' : isSubmitting || attachTask.isPending ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              )}
            </div>
  )

  const drawerHeader = (
    <div className="sticky top-0 z-10 flex shrink-0 flex-row items-center justify-between gap-3 bg-surface-L0 p-4">
      <h2 className="truncate text-lg font-semibold text-content-primary-bold">
        {titleNode}
      </h2>
      {headerActions}
    </div>
  )
  const drawerFooter = (
    <div
      className={cn(
        'sticky bottom-0 z-10 flex flex-col gap-2 bg-surface-L0 p-4 pb-[calc(1rem+var(--safe-bottom))]',
        { hidden: footerHidden },
      )}
    >
      {footerContent}
    </div>
  )

  return (
    <>
      {isDesktop ? (
        isOpen && (
          // Desktop: centered modal dialog instead of the bottom-sheet.
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-6"
            onMouseDown={handleCancel}
          >
            <div
              className="flex max-h-[85vh] w-[760px] max-w-full flex-col overflow-hidden rounded-2xl bg-surface-L0 shadow-xl"
              onMouseDown={(e) => e.stopPropagation()}
            >
              {drawerHeader}
              {summaryNode}
              {stepIndicatorNode}
              <div className="flex-1 overflow-y-auto px-4 pb-4">
                {contentHidden ? skeletonContent : scrollContent}
              </div>
              {drawerFooter}
            </div>
          </div>
        )
      ) : (
        // Mobile: keep the drawer mounted and let it manage its own
        // slide-in/out via the `open` prop.
        <ResizableDrawer
          open={isOpen}
          snapPoints={resolvedSnapPoints}
          activeSnapPoint={activeSnap ?? undefined}
          onActiveSnapPointChange={setActiveSnap}
          fitContent={isCreateMode}
          overlay={isCreateMode || (isEditMode && !isMapVisible)}
          onOverlayClick={handleCancel}
          className="overflow-hidden"
        >
          {drawerHeader}
          {summaryNode}
          {stepIndicatorNode}
          <div className="px-4 pb-4">
            {contentHidden ? skeletonContent : scrollContent}
          </div>
          {drawerFooter}
        </ResizableDrawer>
      )}

      {/* Departure Airport Picker  */}
      {/* Aircraft Picker */}
      <AircraftPickerDrawer
        isOpen={isAircraftPickerOpen}
        onClose={() => setIsAircraftPickerOpen(false)}
        onSelect={(aircraftId) =>
          setValue('aircraftId', aircraftId, {
            shouldValidate: true,
            shouldTouch: true,
            shouldDirty: true,
          })
        }
        selectedAircraftId={watchedAircraftId}
        // Selectable even while in maintenance — the departure-date window check
        // (maintenanceConflict) blocks the save when the dates actually clash.
        allowMaintenance
      />
      <AirportPickerDrawer
        isOpen={isDepartureAirportPickerOpen}
        onClose={() => setIsDepartureAirportPickerOpen(false)}
        onSelect={(icaoCode) => {
          userTouchedScheduleRef.current = true
          // A different airport invalidates the FBO picked for the old one.
          if (icaoCode !== departureAirportIcao) {
            setValue('departureFboId', null, { shouldDirty: true })
          }
          setValue('departureAirportIcao', icaoCode, {
            shouldValidate: true,
            shouldTouch: true,
            shouldDirty: true,
          })
        }}
        title="Departure Location"
        searchParam={departureAirport?.icaoCode}
      />
      {/* Arrival Airport Picker  */}
      <AirportPickerDrawer
        isOpen={isArrivalAirportPickerOpen}
        onClose={() => setIsArrivalAirportPickerOpen(false)}
        onSelect={(icaoCode) => {
          userTouchedScheduleRef.current = true
          // A different airport invalidates the FBO picked for the old one.
          if (icaoCode !== arrivalAirportIcao) {
            setValue('arrivalFboId', null, { shouldDirty: true })
          }
          setValue('arrivalAirportIcao', icaoCode, {
            shouldValidate: true,
            shouldTouch: true,
            shouldDirty: true,
          })
        }}
        title="Arrival Location"
        searchParam={arrivalAirport?.icaoCode}
      />

      {/* Airport info drawer — stacks over the flight form (no navigation) */}
      <AirportDetailDrawer
        icao={infoIcao}
        isOpen={infoIcao != null}
        onClose={() => setInfoIcao(null)}
      />

      {/* Flight log drawer — pilots crewed on the leg edit (even when the
          flight itself is read-only for them) once it has departed; admins
          and other pilots view read-only. Never on drafts. */}
      {tripLegId && !isDraft && (isAdmin || isPilot) && (
        <FlightLogDrawer
          isOpen={isFlightLogOpen}
          onClose={() => setIsFlightLogOpen(false)}
          tripLegId={tripLegId}
          readOnly={!isPilotOnLeg || !canLogLeg}
        />
      )}

      {/* Crew Picker Drawer */}
      <CrewMemberPickerDrawer
        isOpen={isCrewPickerOpen}
        onClose={() => setIsCrewPickerOpen(false)}
        mode="multiple"
        selectedCrewIds={crewIds}
        onSelectMultiple={handleSelectCrew}
        title="Select Crew"
        subtitle={
          departureDate
            ? [
                departureAirport?.icaoCode && arrivalAirport?.icaoCode
                  ? `${departureAirport.icaoCode} → ${arrivalAirport.icaoCode}`
                  : departureAirport?.icaoCode,
                aircraftRegistration,
                dayjs(departureDate).format('ddd, D MMM YYYY'),
              ]
                .filter(Boolean)
                .join(' · ')
            : undefined
        }
        availableCrew={availableCrewData?.availableCrew?.map(({ crew, dutyStatus }) => ({
          ...crew,
          dutyStatus,
        }))}
        unavailableCrew={availableCrewData?.unavailableCrew?.map(({ crew, dutyStatus, reason, otherAircraftRegistration }) => ({
          ...crew,
          dutyStatus: dutyStatus ?? 'OffDuty',
          reason,
          otherAircraftRegistration,
        }))}
        onAssignOnDuty={
          isAdmin && !readOnly && departureDateStr && watchedAircraftId
            ? (crewId: string) =>
                setPendingOnDutyCrewIds((prev) => new Set(prev).add(crewId))
            : undefined
        }
        isLoading={isAvailableCrewLoading}
      />

      {/* PAX Picker Drawer */}
      <PassengerPickerDrawer
        isOpen={isPaxPickerOpen}
        onClose={() => setIsPaxPickerOpen(false)}
        mode="multiple"
        selectedPassengerIds={paxIds}
        unknownPaxCount={unknownPaxCount}
        onSelectMultiple={handleSelectPax}
        title="Select Passengers"
      />

      {/* Add-passenger drawer (PAX step) — new pax is selected on the flight */}
      <PaxDrawer
        isOpen={isAddPaxOpen}
        onClose={() => setIsAddPaxOpen(false)}
        onSuccess={(newPaxId) => {
          if (newPaxId && !paxIds.includes(newPaxId)) {
            handleSelectPax([...paxIds, newPaxId])
          }
        }}
      />

      {/* Crew Basic Detail Drawer */}
      <CrewBasicDetailDrawer
        isOpen={crewDetailId !== null}
        onClose={() => setCrewDetailId(null)}
        crewId={crewDetailId}
      />

      {/* PAX Basic Detail Drawer */}
      <PaxBasicDetailDrawer
        isOpen={paxDetailId !== null}
        onClose={() => setPaxDetailId(null)}
        passengerId={paxDetailId}
      />

      {/* Task Picker Drawer */}
      {((isEditMode && tripLegId) || isCreateMode) && (
        <TaskPickerDrawer
          isOpen={isTaskPickerOpen}
          onClose={() => setIsTaskPickerOpen(false)}
          selectedTemplateIds={selectedTemplateIds}
          onSelect={(ids) => setSelectedTemplateIds(ids)}
        />
      )}

      {/* Flight document drawers: chooser → upload new / attach existing, plus viewer */}
      <TripAddDocumentDrawer
        isOpen={isAddDocumentChooserOpen}
        onClose={() => setIsAddDocumentChooserOpen(false)}
        onSelectDocuments={() => setIsAttachDocumentsOpen(true)}
        onSelectUpload={() => setIsUploadDocumentOpen(true)}
      />

      <DocumentFormDrawer
        isOpen={isUploadDocumentOpen}
        onClose={() => setIsUploadDocumentOpen(false)}
        owner={{ type: 'trip', tripLegId: isEditMode ? tripLegId : undefined }}
        onStage={
          isCreateMode
            ? (doc) => setStagedDocuments((prev) => [...prev, doc])
            : undefined
        }
      />

      <AddComplianceDocumentToTripDrawer
        isOpen={isAttachDocumentsOpen}
        onClose={() => setIsAttachDocumentsOpen(false)}
        tripLegId={isEditMode ? tripLegId : undefined}
        stagedSelectedIds={isCreateMode ? stagedExistingDocIds : undefined}
        onStageSelection={isCreateMode ? setStagedExistingDocIds : undefined}
      />

      <ViewDocumentDrawer
        isOpen={isViewDocumentOpen}
        onClose={() => setIsViewDocumentOpen(false)}
        documentId={viewDocumentId}
        readOnly={true}
        onRemove={
          isEditMode && !readOnly && !isCompleted && viewDocumentId
            ? async () => {
                const doc = legDocuments.find((d) => d.id === viewDocumentId)
                setRemoveDocTarget({ id: viewDocumentId, title: doc?.title || 'this document' })
              }
            : undefined
        }
      />

      {/* Remove Document Confirmation Dialog (edit mode) */}
      <AlertDialog
        open={removeDocTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveDocTarget(null)
        }}
      >
        <AlertDialogContent className="bg-surface-L0">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {removeDocTarget?.title ? `"${removeDocTarget.title}"` : 'this document'} from this flight?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="secondary"
              size="mini"
              onClick={() => setRemoveDocTarget(null)}
              disabled={updateLegDocuments.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="mini"
              onClick={confirmRemoveDocument}
              disabled={updateLegDocuments.isPending}
            >
              {updateLegDocuments.isPending ? 'Removing...' : 'Remove'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add-on-duty confirmation (create-mode inline crew list) */}
      <AlertDialog open={assignTarget !== null} onOpenChange={(open) => !open && setAssignTarget(null)}>
        <AlertDialogContent className="bg-surface-L0">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {assignTarget?.otherAircraftRegistration ? 'Reassign to This Aircraft?' : 'Add On-Duty Status?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {assignTarget
                ? assignTarget.otherAircraftRegistration
                  ? `${assignTarget.name} is On Duty on ${assignTarget.otherAircraftRegistration}. This moves their roster to this flight's aircraft when you save.`
                  : `${assignTarget.name} is off duty. They'll be added to the roster as On Duty when you save this flight.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="secondary" size="mini" onClick={() => setAssignTarget(null)}>
              Cancel
            </Button>
            <Button size="mini" onClick={handleConfirmAssignOnDuty}>
              {assignTarget?.otherAircraftRegistration ? 'Reassign' : 'Add on Duty'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className='bg-surface-L0'>
          <AlertDialogHeader>
            <AlertDialogTitle>{removeFlightLabel}</AlertDialogTitle>
            <AlertDialogDescription>
              {isDraft
                ? 'This will remove this flight from the trip.'
                : 'This will permanently cancel this flight from the trip.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="secondary"
              size="mini"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={deleteTripLeg.isPending}
            >
              Close
            </Button>
            <Button
              variant="destructive"
              size="mini"
              onClick={handleRemoveFlight}
              disabled={deleteTripLeg.isPending}
            >
              {deleteTripLeg.isPending
                ? isDraft ? 'Removing...' : 'Canceling...'
                : isDraft ? 'Remove' : 'Cancel'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
